/**
 * Centralized Projects Configuration
 * 
 * Single source of truth for all projects in the application.
 * Used by routing, navigation, and project index components.
 * 
 * Projects are lazy-loaded to improve initial bundle size and performance.
 */

import { lazy } from 'react';
import { Project } from '../types';

// Lazy load all project components
const HelloWorld = lazy(() => import('../projects/hello-world'));
const TypingTest = lazy(() => import('../projects/typing-test'));
const StartupAwakening = lazy(() => import('../projects/startup-awakening'));
const LanguageStats = lazy(() => import('../projects/language-stats'));
const SkyDefense = lazy(() => import('../projects/sky-defense-v2'));
const TurtleQuest = lazy(() => import('../projects/turtle-quest'));
const Game2048 = lazy(() => import('../projects/2048'));
const BettingPool = lazy(() => import('../projects/betting-pool'));
const SpriteDebugger = lazy(() => import('../projects/sprite-debugger'));

// Component import functions for preloading
export const PROJECT_IMPORTS = {
  '2048': () => import('../projects/2048'),
  'betting-pool': () => import('../projects/betting-pool'),
  'hello-world': () => import('../projects/hello-world'),
  'language-stats': () => import('../projects/language-stats'),
  'sky-defense': () => import('../projects/sky-defense-v2'),
  'sprite-debugger': () => import('../projects/sprite-debugger'),
  'startup-awakening': () => import('../projects/startup-awakening'),
  'turtle-quest': () => import('../projects/turtle-quest'),
  'typing-test': () => import('../projects/typing-test'),
} as const;

export const PROJECTS: Project[] = [
  {
    id: '2048',
    name: '2048 Game',
    description: 'A sliding block puzzle game where you combine tiles to reach 2048',
    path: '/projects/2048',
    component: Game2048
  },
  {
    id: 'betting-pool',
    name: 'Betting Pool',
    description: 'Track daily user growth and see who predicted closest to actual install numbers',
    path: '/projects/betting-pool',
    component: BettingPool
  },
  {
    id: 'hello-world',
    name: 'Hello World',
    description: 'A sample AI experiment demonstrating basic project structure',
    path: '/projects/hello-world',
    component: HelloWorld
  },
  {
    id: 'language-stats',
    name: 'Language Statistics',
    description: 'Interactive visualization of programming languages used in the repository',
    path: '/projects/language-stats',
    component: LanguageStats
  },
  {
    id: 'sky-defense',
    name: 'Sky Defense v2',
    description: 'Enhanced 2D side-scrolling shooter with scene-based movement, level progression, and health-based gameplay',
    path: '/projects/sky-defense',
    component: SkyDefense
  },
  {
    id: 'sprite-debugger',
    name: 'Sprite Debugger',
    description: 'A pixel sprite debugger for fixing sprite sheets with incorrect layouts. Upload, configure, and test sprite animations.',
    path: '/projects/sprite-debugger',
    component: SpriteDebugger
  },
  {
    id: 'startup-awakening',
    name: 'Startup Awakening',
    description: 'A startup simulation game where you navigate the challenges of building a tech company',
    path: '/projects/startup-awakening',
    component: StartupAwakening
  },
  {
    id: 'turtle-quest',
    name: 'Turtle Quest',
    description: 'A 2D adventure game featuring a turtle hero exploring an island with inventory management and combat',
    path: '/projects/turtle-quest',
    component: TurtleQuest
  },
  {
    id: 'typing-test',
    name: 'Typing Test',
    description: 'Test your typing speed and accuracy with real-time feedback',
    path: '/projects/typing-test',
    component: TypingTest
  }
];

// Helper functions for type safety
export const getProjectById = (id: string): Project | undefined => {
  return PROJECTS.find(project => project.id === id);
};

export const getProjectByPath = (path: string): Project | undefined => {
  return PROJECTS.find(project => project.path === path);
};